import * as React from "react"
import { useQueries } from "@tanstack/react-query"

import { orderHandoffDefaultsQueryOptions } from "@/lib/queries"
import { handoffDefaultsFromApi } from "@/features/marketplace/components/MarketplaceOrderHandoffDefaultsForm"

import type { MarketplaceOrderHandoffDefaults } from "@nexus/contracts"
import type { PartnerSend } from "@/features/marketplace/lib/partner-send-plan"

export interface ReviewDraft {
  to: string
  cc: string
  subject: string
  canSend: boolean
}

export const EMPTY_REVIEW_DRAFT: ReviewDraft = { to: "", cc: "", subject: "", canSend: true }

/**
 * One review-email draft per partner, seeded from that partner's own contact
 * emails, so a multi-partner send never mails one partner's recipients to another.
 */
export function usePartnerReviewDrafts(
  tenantId: string | null,
  partnerSends: readonly PartnerSend[],
) {
  const [drafts, setDrafts] = React.useState<Record<string, ReviewDraft>>({})
  // Seeding is tracked apart from the draft: the review step can report canSend before contacts load.
  const seededRef = React.useRef(new Set<string>())
  const previewQueries = useQueries({
    queries: partnerSends.map((send) =>
      orderHandoffDefaultsQueryOptions(tenantId, send.connectionId),
    ),
  })
  const previewsKey = previewQueries.map((q) => q.dataUpdatedAt).join(",")

  React.useEffect(() => {
    previewQueries.forEach((query, index) => {
      const connectionId = partnerSends[index]?.connectionId
      const preview = query.data
      if (!connectionId || !preview || !("recipients" in preview)) return
      if (seededRef.current.has(connectionId)) return
      seededRef.current.add(connectionId)
      setDrafts((current) => {
        const draft = current[connectionId] ?? EMPTY_REVIEW_DRAFT
        return {
          ...current,
          [connectionId]: {
            ...draft,
            to: draft.to || preview.recipients.to.join(", "),
            cc: draft.cc || preview.recipients.cc.join(", "),
          },
        }
      })
    })
  }, [previewsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  function draftFor(connectionId: string): ReviewDraft {
    return drafts[connectionId] ?? EMPTY_REVIEW_DRAFT
  }

  /** The partner's own handoff defaults (its package values), once loaded. */
  function defaultsFor(connectionId: string): MarketplaceOrderHandoffDefaults | null {
    const index = partnerSends.findIndex((send) => send.connectionId === connectionId)
    const preview = index >= 0 ? previewQueries[index]?.data : undefined
    if (!preview) return null
    return handoffDefaultsFromApi("defaults" in preview ? preview.defaults : preview)
  }

  function updateDraft(connectionId: string, patch: Partial<ReviewDraft>) {
    setDrafts((current) => {
      const draft = current[connectionId] ?? EMPTY_REVIEW_DRAFT
      const isUnchanged = (Object.keys(patch) as (keyof ReviewDraft)[]).every(
        (key) => draft[key] === patch[key],
      )
      // The review step reports canSend from an effect on every render; a new
      // object here would re-render it and loop.
      if (isUnchanged && current[connectionId]) return current
      return { ...current, [connectionId]: { ...draft, ...patch } }
    })
  }

  function resetDrafts() {
    seededRef.current.clear()
    setDrafts({})
  }

  return { draftFor, defaultsFor, updateDraft, resetDrafts }
}
